<template>
  <Dialog
    :visible="visible"
    :header="title"
    :modal="true"
    :style="{ width: '600px' }"
    :content-style="{ minHeight: '350px', position: 'relative' }"
    :closable="!isLoading"
    @update:visible="$emit('update:visible', $event)"
  >
    <Loader v-if="isLoading" />
    <div
      v-else
      class="loop-config-form p-fluid"
    >
      <div class="field">
        <label class="block mb-2 text-left">
          <i class="far fa-layer-group mr-2" />
          {{ t('templateBuilder.loop.collection') }} <span class="text-red-500">*</span>
        </label>
        <Dropdown
          v-model="localCollection"
          :options="collectionGroups"
          option-group-label="label"
          option-group-children="items"
          option-label="label"
          option-value="value"
          :placeholder="t('templateBuilder.loop.selectCollection')"
          :filter="true"
          class="w-full"
          @change="onCollectionChange"
        >
          <template #value="{ value }">
            <div
              v-if="value"
              class="text-left"
            >
              {{ getCollectionLabel(value) }}
            </div>
          </template>
          <template #optiongroup="{ option }">
            <div class="flex align-items-center">
              <span>{{ option.label }}</span>
            </div>
          </template>
          <template #option="{ option }">
            <div class="flex align-items-center justify-content-between w-full">
              <div class="flex align-items-center gap-2">
                <i
                  v-if="option.isCustom"
                  class="fa-solid fa-puzzle-piece"
                  style="color: #8b5cf6; font-size: 0.85rem;"
                />
                <span>{{ option.label }}</span>
              </div>
              <span
                v-if="option.isCustom"
                class="custom-badge"
              >
                CUSTOM
              </span>
            </div>
          </template>
        </Dropdown>
      </div>

      <div class="field">
        <label class="block mb-2 text-left">
          <i class="far fa-code mr-2" />
          {{ t('templateBuilder.loop.iterator') }} <span class="text-red-500">*</span> <i
            v-tooltip="{ value: t('templateBuilder.loop.iteratorHelp'), class: 'tooltip-icons-outside' }"
            class="fas fa-question-circle"
          />
        </label>
        <InputText
          v-model="localIterator"
          :placeholder="t('templateBuilder.loop.iteratorPlaceholder')"
          class="w-full"
        />
      </div>

      <div class="field">
        <label class="block mb-2 text-left">
          <i class="far fa-sort mr-2" />
          {{ t('templateBuilder.loop.orderLabel') }} <span class="text-red-500">*</span> <i
            v-tooltip="{ value: t('templateBuilder.loop.orderHelp'), class: 'tooltip-icons-outside' }"
            class="fas fa-question-circle"
          />
        </label>
        <div class="flex gap-2">
          <Dropdown
            v-model="localOrderField"
            :options="availableFields"
            option-label="label"
            option-value="value"
            :placeholder="t('templateBuilder.loop.selectColumn')"
            :filter="true"
            class="flex-1"
            :disabled="!localCollection || availableFields.length === 0"
          />
          <Dropdown
            v-model="localOrderDirection"
            :options="orderDirections"
            option-label="label"
            option-value="value"
            :placeholder="t('templateBuilder.loop.order')"
            class="w-8rem"
          />
        </div>
      </div>

      <div class="field">
        <label class="block mb-2 text-left">
          <i class="far fa-list-ol mr-2" />
          {{ t('templateBuilder.loop.limitLabel') }} <span class="text-red-500">*</span> <i
            v-tooltip="{ value: t('templateBuilder.loop.limitHelp'), class: 'tooltip-icons-outside' }"
            class="fas fa-question-circle"
          />
        </label>
        <InputNumber
          v-model="localLimit"
          :min="1"
          :max="1000"
          placeholder="10"
          class="w-full"
        />
      </div>

      <div
        v-if="localCollection && localIterator"
        class="preview-section mt-3 p-3"
      >
        <div class="preview-label mb-2">
          <i class="far fa-eye mr-2" />
          {{ t('templateBuilder.loop.preview') }}
        </div>
        <code class="preview-code">
          {{ `{var=${localIterator}.property}` }}
        </code>
      </div>
    </div>

    <template #footer>
      <Button
        :label="t('common.cancel')"
        icon="far fa-times"
        class="p-button-secondary"
        :disabled="isLoading"
        @click="handleCancel"
      />
      <Button
        v-if="showDeleteButton"
        :label="t('common.delete')"
        icon="far fa-trash-alt"
        class="p-button-danger"
        :disabled="isLoading"
        @click="handleDelete"
      />
      <Button
        :label="t('common.save')"
        icon="far fa-check"
        :disabled="!localCollection || !localIterator || !localOrderField || !localLimit || isLoading"
        :loading="isLoading"
        @click="handleSave"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, ref, watch, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import Loader from '@/components/layout/Loader.vue';
import { getAvailableCollections, getDefaultIterator } from '@/composables/template-builder/loopCollections';
import type { CollectionGroup, LoopConfiguration } from '@/types/loop-configuration';
import { getCollectionSchema } from '@/composables/template-builder/collectionContext';
import Tooltip from 'primevue/tooltip';

export default defineComponent({
  name: 'LoopConfigurationModal',

  components: {
    Dialog,
    Dropdown,
    InputText,
    InputNumber,
    Button,
    Loader,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },

    modelValue: {
      type: Object as PropType<LoopConfiguration | null>,
      default: null,
    },

    title: {
      type: String,
      default: '',
    },

    showDeleteButton: {
      type: Boolean,
      default: false,
    },

    contexts: {
      type: Array as PropType<string[]>,
      default: () => ['contact', 'order', 'cart', 'product', 'visitor'],
    },
  },

  emits: {
    'update:visible': (value: boolean) => typeof value === 'boolean',
    save: (config: LoopConfiguration) => config !== null,
    delete: () => true,
  },

  setup(props, { emit }) {
    const { t } = useI18n();

    const localCollection = ref('');
    const localIterator = ref('');
    const localOrderField = ref('');
    const localOrderDirection = ref<'asc' | 'desc'>('asc');
    const localLimit = ref<number>(10);
    const collectionGroups = ref<CollectionGroup[]>([]);
    const originalIterator = ref('');
    const isLoading = ref(false);
    const availableFields = ref<{ label: string; value: string }[]>([]);

    const orderDirections = [
      { label: t('templateBuilder.loop.ascending'), value: 'asc' },
      { label: t('templateBuilder.loop.descending'), value: 'desc' },
    ];

    // Load available fields when collection changes
    const loadAvailableFields = async () => {
      if (!localCollection.value) {
        availableFields.value = [];
        return;
      }

      try {
        const { schema } = await getCollectionSchema(localCollection.value);
        if (schema && schema.properties) {
          availableFields.value = schema.properties.map((prop: any) => ({
            label: prop.label || prop.name,
            value: prop.name,
          }));
        } else {
          availableFields.value = [];
        }
      } catch (error) {
        console.error('Error loading fields:', error);
        availableFields.value = [];
      }
    };

    // Load collections when modal opens
    watch(() => props.visible, async (isVisible) => {
      if (isVisible) {
        isLoading.value = true;
        try {
          // Load available collections
          const collections = await getAvailableCollections(props.contexts);
          collectionGroups.value = collections;

          // Initialize form with modelValue
          if (props.modelValue) {
            localCollection.value = props.modelValue.collection;
            localIterator.value = props.modelValue.iterator;
            originalIterator.value = props.modelValue.iterator;
            localOrderField.value = props.modelValue.order?.field || '';
            localOrderDirection.value = props.modelValue.order?.direction || 'asc';
            localLimit.value = props.modelValue.limit || 10;
          } else {
            localCollection.value = '';
            localIterator.value = '';
            originalIterator.value = '';
            localOrderField.value = '';
            localOrderDirection.value = 'asc';
            localLimit.value = 10;
          }

          // Load fields for the selected collection
          if (localCollection.value) {
            await loadAvailableFields();
          }
        } finally {
          isLoading.value = false;
        }
      }
    });

    // Auto-fill iterator when collection changes
    const onCollectionChange = async () => {
      if (localCollection.value) {
        const selectedCollection = collectionGroups.value
          .flatMap((group) => group.items)
          .find((item) => item.value === localCollection.value);

        if (selectedCollection) {
          localIterator.value = selectedCollection.defaultIterator;
        } else {
          localIterator.value = getDefaultIterator(localCollection.value);
        }

        // Load available fields for the new collection
        await loadAvailableFields();

        // Reset order field if it's not in the new collection's fields
        if (localOrderField.value && availableFields.value.length > 0) {
          const fieldExists = availableFields.value.some((f) => f.value === localOrderField.value);
          if (!fieldExists) {
            localOrderField.value = '';
          }
        }
      }
    };

    const handleSave = async () => {
      if (localCollection.value && localIterator.value) {
        isLoading.value = true;
        try {
          const saveConfig: LoopConfiguration = {
            enabled: true,
            collection: localCollection.value,
            iterator: localIterator.value,
            limit: localLimit.value || 10,
          };

          // Add order if field is selected
          if (localOrderField.value) {
            saveConfig.order = {
              field: localOrderField.value,
              direction: localOrderDirection.value,
            };
          }

          emit('save', saveConfig);

          // Wait a bit to ensure the save event is processed before closing
          await new Promise((resolve) => setTimeout(resolve, 50));

          emit('update:visible', false);
        } finally {
          isLoading.value = false;
        }
      }
    };

    const handleDelete = () => {
      emit('delete');
      emit('update:visible', false);
    };

    const handleCancel = () => {
      emit('update:visible', false);
    };

    const getCollectionLabel = (value: string) => {
      const collection = collectionGroups.value
        .flatMap((group) => group.items)
        .find((item) => item.value === value);
      return collection?.label || value;
    };

    return {
      t,
      localCollection,
      localIterator,
      localOrderField,
      localOrderDirection,
      localLimit,
      collectionGroups,
      availableFields,
      orderDirections,
      isLoading,
      onCollectionChange,
      handleSave,
      handleDelete,
      handleCancel,
      getCollectionLabel,
    };
  },
});
</script>

<style scoped lang="scss">
.loop-config-form {
  :deep(.p-dropdown .p-dropdown-label) {
    text-align: left;
  }
}

.custom-badge {
  background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
  text-transform: uppercase;
  white-space: nowrap;
}

.preview-section {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.preview-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
}

.preview-code {
  display: block;
  background: #ffffff;
  padding: 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  color: #8b5cf6;
}
</style>
